import { BankAccount } from "."
import { Network } from "../../.."
import store from "../../../../store"
import { formatDate, getCurrency } from "../../../formating"
import URLSearchParamsExtended from "../../../URLSearchParams"

// transaction categories
export const TRANSACTION_CATEGORY = {
	ROLLOVER: "ROLLOVER",
	ADJUSTMENT: "ADJUSTMENT",
	ADJUSTMENT_AGAINST_CONTRIBUTION_LIMIT: "ADJUSTMENT_AGAINST_CONTRIBUTION_LIMIT",
	PAYROLL_CONTRIBUTION: "PAYROLL_CONTRIBUTION",
	EMPLOYER_CONTRIBUTION: "EMPLOYER_CONTRIBUTION",
	EMPLOYER_REIMBURSEMENT: "EMPLOYER_REIMBURSEMENT",
	PAYROLL_REIMBURSEMENT: "PAYROLL_REIMBURSEMENT",
	ACH_REIMBURSEMENT: "ACH_REIMBURSEMENT",
	ACH_WITHDRAWAL: "ACH_WITHDRAWAL",
	ACH_DEPOSIT: "ACH_DEPOSIT",
}

export const TRANSACTION_TYPE = {
	WITHDRAWAL: "Withdrawal",
	CONTRIBUTION: "Contribution",
	ALLOCATION: "Allocation",
	REIMBURSEMENT: "Reimbursement"
}

export class TransactionAttributes {
	constructor(attributes = {}) {
		Object.assign(this, attributes)
	}
}

export class Transaction extends TransactionAttributes {
	static async getDailyActivity({ params, user }) {
		const [{ data: response }] = await Promise.all([
			Network.AxiosWrapperV2.get({
				url: `dailyActivity/transaction?${params}&included=bankAccount`
			})
		])
		const data = response?.data
		return new Map(
			data.map((t) => [
				t.id,
				new this(
					t,
					{ token: user.token, ...response.included.users[t.userId] },
					new BankAccount(response.included.bankAccounts[t?.bankAccountId] || {})
				)
			])
		)
	}

	static async getNachaTransactions({ params, user }) {
		const [{ data: response }] = await Promise.all([
			Network.AxiosWrapperV2.get({
				url: `dailyActivity/nachaTransaction?${params}`
			})
		])
		const data = response?.data
		return new Map(
			data.map((t) => [
				t.id,
				new this(
					t,
					{ token: user.token, ...response.included.users[t.userId] }
				)
			])
		)
	}

	static async getAll({ params, user, bankAccounts }) {
		const [{ data: response }, bankAccountMap] = await Promise.all([
			Network.AxiosWrapperV2.get({
				url: "transaction/v2?" + new URLSearchParamsExtended(params).toString()
			}),
			bankAccounts || BankAccount.getAll({})
		])
		const data = response?.data || response.transactions
		return new Map(
			data.map((t) => [
				t.id,
				new this(t, user, bankAccountMap.get(t.serviceId || t.bankAccountId))
			])
		)
	}

	constructor(attributes, user, bankAccount) {
		super()
		Object.assign(this, attributes)

		this.user = new Network.API.Employer.Employee(user)
		this.account = bankAccount || new BankAccount(attributes.account)
		this.patient = {
			firstName: attributes.patient?.firstName,
			lastName: attributes.patient?.lastName,
			name: `${attributes.patient?.firstName} ${attributes.patient?.lastName}`
		}
	}

	get isNewRecord() {
		return this.id === "add" || !this.id
	}

	get realStatus() {
		const statusLong = this.status?.long || ""
		const statusShort = this.status?.short || ""
		const statusId = this.status?.id || ""
		let shouldShowPopover = false

		let ret = statusShort || statusLong || statusId

		if (ret !== statusLong && Boolean(statusLong)) shouldShowPopover = true

		return {
			display: ret,
			shouldShowPopover
		}
	}

	get tableValues() {
		return {
			plan: this?.plan?.type,
			date: formatDate({
				data: this.effectiveDate,
				actions: ["display"]
			}),
			createdDate: formatDate({
				data: this.createdTs,
				actions: ["display+time"]
			}),
			employee: `${this.user.firstName} ${this.user.lastName}`,
			employer: this.employer ? this.employer.name : this.user?.employer?.name?.substring(0, 20),
			payrollId: this.user.payroll?.displayId,
			applicationId: this.user.payroll?.applicationId,
			email: this.user.email,
			account: this.account,
			amount: getCurrency({
				amount: this.amount,
				actions: ["divide"]
			}),
			type: this.cashflowType,
			description: this.description,
			provider: this.provider?.name || "",
			patient: this.patient.name,
			dateOfService: this.serviceDate
				? formatDate({
					data: this.serviceDate,
					actions: ["display"]
				  })
				: "",
			status: this.status?.short,
			realStatus: this.status?.long || this.status?.short,
			statusDate: this.status?.ts
				? formatDate({
					data: this.status.ts,
					actions: ["display"]
				  })
				: "",
			labels: {
				dateOfService: this.serviceDate ? "Date Of Service: " : "",
			}
		}
	}

	get changeHoldMessage() {
		if (!this.links.hold?.message) {
			return this.holdTs ? "Remove hold on this transaction?" : "Place hold on this transaction?"
		}
		return this.links.hold?.message
	}

	get hold() {
		return {
			date: this.holdTs && formatDate({
				data: this.holdTs,
				actions: ["display"]
			}),
			changeMessage: this.changeHoldMessage,
			icon: this.holdTs ? "update" : "watch_later",
			color: this.holdTs ? "warning" : "success"
		}
	}

	async cancel() {
		if (this.links.delete) {
			try {
				await Network.AxiosWrapperV2.send(this.links.delete)
				store.setNotification("notification", true, {
					msg: `${this.tableValues.description || this.tableValues.type} canceled for ${this.tableValues.amount}.`,
					severity: "success",
					isPersist: true
				})
				return true
			} catch (err) {
				console.error(err)
			}
			return false
		}
	}

	async changeHold({ note }) {
		const message = this.holdTs
			? `Hold removed for ${this.tableValues.description || this.tableValues.type} belonging to ${this.tableValues.employee}.`
			: `Hold added for ${this.tableValues.description || this.tableValues.type} belonging to ${this.tableValues.employee}.`

		if (this.links.hold) {
			return await Network.AxiosWrapperV2.send({
				...this.links.hold,
				data: { note }
			})
				.then(() => {
					store.setNotification("notification", true, {
						msg: message,
						severity: "success",
						isPersist: true
					})
				})
				.catch((e) => {
					store.setNotification("notification", true, {
						msg: e.response?.data.message,
						severity: "error",
						isPersist: true
					})
				})
		}
	}

	async createNACHA() {
		if (Boolean(this.links.nacha)) {
			try {
				await Network.AxiosWrapperV2.send(this.links.nacha)
				store.setNotification("notification", true, {
					msg: `NACHA file successfully created for transaction.`,
					severity: "success"
				})
			} catch (error) {
				console.error(error)
			}
		}
	}
}

export default Transaction
